import { useEffect, useState, useCallback } from 'react'
import { Award, Plus, Edit3, Trash2, X, UserPlus, Search } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import { badgeAPI, playersAPI } from '@/lib/api'

/* Beheer van badges: types aanmaken/aanpassen/wissen en handmatig aan een
   user toekennen. Shows (Kempenvision, Image Compo) hebben geen automatische
   punten meer, dus een winnaar-badge is nu de manier om resultaten van die
   compo's aan een specifieke user te hangen. */

/* `icon_url` kan drie vormen zijn: emoji-tekst ("🔥"), data-URI SVG, of
   externe URL. `<img src=…>` werkt alleen voor de laatste twee — emoji
   moet als tekst gerenderd worden. */
function BadgeIcon({ icon, color, size = 'h-6 w-6' }) {
  const isBeeld = typeof icon === 'string'
    && (icon.startsWith('http') || icon.startsWith('data:') || icon.startsWith('/'))
  if (icon && !isBeeld) {
    return <span className={`${size} grid place-items-center text-lg leading-none`}>{icon}</span>
  }
  if (isBeeld) {
    return <img src={icon} alt="" className={`${size} object-contain`} />
  }
  return <Award className="h-4 w-4" style={{ color }} />
}

const KLEUR_KEUZE = [
  '#B4FF39', '#61E294', '#3adf3a', '#00d9ff', '#337cff',
  '#8b3fff', '#c77bff', '#ff2b8f', '#ff5a5a', '#ff9500',
  '#ffcf40', '#E4C265', '#ffffff', '#94a3b8',
]

export default function BadgeAdminPage() {
  const [types, setTypes]     = useState([])
  const [awards, setAwards]   = useState([])
  const [typeDlg, setTypeDlg] = useState(null)      // { id?, name, description, color, icon_url }
  const [awardDlg, setAwardDlg] = useState(null)    // { badgeTypeId, badgeName, color }
  const [zoekQ, setZoekQ]     = useState('')
  const [zoekLijst, setZoekLijst] = useState([])
  const [error, setError]     = useState(null)

  const load = useCallback(() => {
    badgeAPI.getAllTypes().then(r => setTypes(r.data?.badges ?? [])).catch(e => setError(e.response?.data?.error ?? e.message))
    badgeAPI.listRecentAwards(50).then(r => setAwards(r.data?.awards ?? [])).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  // Live user-search voor het award-dialoog.
  useEffect(() => {
    if (!awardDlg) return
    const t = setTimeout(() => {
      playersAPI.list(zoekQ).then(r => setZoekLijst(r.data?.players ?? r.data?.users ?? [])).catch(() => setZoekLijst([]))
    }, 200)
    return () => clearTimeout(t)
  }, [zoekQ, awardDlg])

  const bewaarType = async (e) => {
    e.preventDefault()
    setError(null)
    const body = {
      name: typeDlg.name, description: typeDlg.description,
      icon_url: typeDlg.icon_url, color: typeDlg.color || '#3b82f6',
      animation: typeDlg.animation || null,
    }
    try {
      if (typeDlg.id) await badgeAPI.updateType(typeDlg.id, body)
      else            await badgeAPI.createType(body)
      setTypeDlg(null); load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wisType = async (id) => {
    if (!window.confirm('Deze badge verwijderen? Ook alle toekenningen ervan gaan weg.')) return
    try { await badgeAPI.deleteBadgeType(id); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const kenToe = async (userId) => {
    try {
      await badgeAPI.awardBadge(userId, awardDlg.badgeTypeId)
      setAwardDlg(null); setZoekQ(''); setZoekLijst([]); load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const trekIn = async (userId, badgeTypeId) => {
    if (!window.confirm('Deze badge terugtrekken bij deze user?')) return
    try { await badgeAPI.revokeBadge(userId, badgeTypeId); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  return (
    <div>
      <PageHeader
        icon={Award}
        title="Badges"
        subtitle="Beheer badge-soorten en ken ze handmatig toe aan spelers"
      />

      {error && <p className="text-sm text-signal mb-3">{error}</p>}

      {/* Types */}
      <Card className="mb-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="h-4 w-4" /> Badge-soorten
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">· {types.length}</span>
          </CardTitle>
          <Button size="sm" onClick={() => setTypeDlg({ name: '', description: '', color: '#B4FF39', icon_url: '' })}>
            <Plus className="h-4 w-4 mr-1" /> Nieuwe badge
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {types.length === 0 && (
            <p className="text-sm text-muted-foreground">Nog geen badges. Maak er een aan om te beginnen.</p>
          )}
          {types.map(b => (
            <div key={b.id} className="flex items-center gap-3 rounded-[4px] border border-border bg-card px-3 py-2"
                 style={{ borderLeft: `3px solid ${b.color}` }}>
              <div className="h-9 w-9 rounded-full grid place-items-center shrink-0 border border-border"
                   style={{ background: `${b.color}22` }}>
                <BadgeIcon icon={b.icon_url} color={b.color} />

              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{b.name}</div>
                {b.description && <div className="text-xs text-muted-foreground truncate">{b.description}</div>}
              </div>
              <Button variant="outline" size="sm"
                      onClick={() => setAwardDlg({ badgeTypeId: b.id, badgeName: b.name, color: b.color })}>
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Toekennen
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setTypeDlg({ id: b.id, name: b.name, description: b.description ?? '',
                                                  color: b.color, icon_url: b.icon_url ?? '',
                                                  animation: b.animation ?? null })}>
                <Edit3 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => wisType(b.id)}>
                <Trash2 className="h-4 w-4 text-signal" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recente toekenningen */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent toegekend
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest ml-2">· {awards.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {awards.length === 0 && <p className="text-sm text-muted-foreground">Nog geen toekenningen.</p>}
          {awards.map(a => (
            <div key={a.id} className="flex items-center gap-3 rounded-[4px] border border-border/40 bg-card/50 px-3 py-1.5">
              <span className="h-6 w-6 rounded-full grid place-items-center shrink-0"
                    style={{ background: `${a.badge_color}22` }}>
                <Award className="h-3 w-3" style={{ color: a.badge_color }} />
              </span>
              <span className="text-sm font-semibold truncate flex-1">
                {a.display_name || a.username}
                <span className="text-muted-foreground font-normal"> · {a.badge_name}</span>
              </span>
              <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                {new Date(a.awarded_at).toLocaleString('nl-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                      onClick={() => trekIn(a.user_id, a.badge_type_id)}>
                <X className="h-3.5 w-3.5 text-signal" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Type-bewerk-dialoog */}
      {typeDlg && (
        <Dialog open onOpenChange={(o) => !o && setTypeDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{typeDlg.id ? 'Badge bewerken' : 'Nieuwe badge'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={bewaarType} className="space-y-3">
              <div>
                <Label>Naam *</Label>
                <Input value={typeDlg.name} required
                       onChange={e => setTypeDlg({ ...typeDlg, name: e.target.value })}
                       placeholder="Kempenvision winnaar" />
              </div>
              <div>
                <Label>Omschrijving</Label>
                <Input value={typeDlg.description}
                       onChange={e => setTypeDlg({ ...typeDlg, description: e.target.value })}
                       placeholder="Won de Kempenvision-show" />
              </div>
              <div>
                <Label>Icoon-URL (optioneel)</Label>
                <Input value={typeDlg.icon_url}
                       onChange={e => setTypeDlg({ ...typeDlg, icon_url: e.target.value })}
                       placeholder="https://…/icon.png" />
              </div>
              <div>
                <Label>Kleur</Label>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <input type="color" value={typeDlg.color}
                         onChange={e => setTypeDlg({ ...typeDlg, color: e.target.value })}
                         className="h-8 w-12 rounded-[4px] border border-border cursor-pointer bg-transparent" />
                  <Input value={typeDlg.color}
                         onChange={e => setTypeDlg({ ...typeDlg, color: e.target.value })}
                         className="flex-1 min-w-[8rem] font-mono" />
                </div>
                <div className="flex gap-1 flex-wrap mt-2">
                  {KLEUR_KEUZE.map(c => (
                    <button key={c} type="button" onClick={() => setTypeDlg({ ...typeDlg, color: c })}
                            className="h-6 w-6 rounded-[3px] border border-border"
                            style={{ background: c,
                                     outline: typeDlg.color?.toLowerCase() === c.toLowerCase() ? '2px solid var(--acid, #B4FF39)' : 'none',
                                     outlineOffset: '1px' }} />
                  ))}
                </div>
              </div>
              <div>
                <Label>Animatie (optioneel)</Label>
                <select value={typeDlg.animation || ''}
                        onChange={e => setTypeDlg({ ...typeDlg, animation: e.target.value || null })}
                        className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
                  <option value="">— statisch —</option>
                  <option value="shine">Shine — glans-band veegt over de kaart</option>
                  <option value="pulse">Pulse — icoon schaalt subtiel + glow</option>
                  <option value="glow">Glow — brede halo ademt in en uit</option>
                  <option value="orbit">Orbit — dashed ring draait rond icoon</option>
                  <option value="spin">Spin — icoon roteert continu</option>
                  <option value="bounce">Bounce — icoon veert op en neer</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Bewaar voor bijzondere badges — respect prefers-reduced-motion.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setTypeDlg(null)}>Annuleer</Button>
                <Button type="submit">{typeDlg.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Award-dialoog */}
      {awardDlg && (
        <Dialog open onOpenChange={(o) => !o && setAwardDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <span className="flex items-center gap-2">
                  <Award className="h-4 w-4" style={{ color: awardDlg.color }} />
                  {awardDlg.badgeName} toekennen aan…
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Zoek deelnemer</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={zoekQ} onChange={e => setZoekQ(e.target.value)} autoFocus
                         placeholder="Naam of username…" className="pl-8" />
                </div>
              </div>
              <div className="max-h-[24rem] overflow-y-auto space-y-1">
                {zoekLijst.length === 0 && (
                  <p className="text-sm text-muted-foreground p-2">Geen deelnemer gevonden.</p>
                )}
                {zoekLijst.slice(0, 30).map(u => (
                  <button key={u.id} type="button" onClick={() => kenToe(u.id)}
                          className="w-full flex items-center gap-3 rounded-[4px] border border-border/40 bg-card px-3 py-2 text-left hover:border-acid/60 transition-colors">
                    <div className="h-8 w-8 rounded-full bg-muted grid place-items-center shrink-0 font-semibold text-xs">
                      {(u.display_name || u.username || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{u.display_name || u.username}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate">@{u.username}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAwardDlg(null)}>Sluit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
